import axios from 'axios'
import { ServiceError, getApiErrorStatus } from '@/utils/apiError'

export type PinConfidence = 'BUILDING' | 'APPROXIMATE'

export interface PlaceCandidate {
  id: string
  name: string
  address: string
  lat: number
  lng: number
  types: string[]
  confidence: PinConfidence
}

type SearchTextResponse = {
  places?: Array<{
    id: string
    displayName?: { text: string }
    formattedAddress?: string
    location?: { latitude: number; longitude: number }
    types?: string[]
  }>
}

/** Tipos do Places que indicam um estabelecimento (ponto do prédio), não um endereço. */
const BUILDING_TYPES = [
  'hospital',
  'doctor',
  'health',
  'pharmacy',
  'dentist',
  'physiotherapist',
  'point_of_interest',
  'establishment',
  'local_government_office'
]

/**
 * Mensagens da busca por nome (serviço externo). As duas mandam o usuário para
 * os planos B do passo 1, mas distinguem "a busca por nome não está habilitada
 * nesta conta" (403/400, não adianta tentar de novo) de "o Google não respondeu".
 */
export const PLACES_UNAVAILABLE_MESSAGE =
  'Busca por nome indisponível. Use o endereço do cadastro ou cole o link do Google Maps.'
export const PLACES_SEARCH_FAILED_MESSAGE =
  'Não foi possível buscar o estabelecimento agora. Tente novamente, use o endereço do cadastro ou cole o link do Google Maps.'

/**
 * Google Places API (New) — Text Search via REST, no mesmo padrão do GeocodingService
 * (chave pública restrita por referrer). Exige "Places API (New)" habilitada na chave.
 */
export default class PlacesService {
  private readonly url = 'https://places.googleapis.com/v1/places:searchText'

  private get apiKey(): string {
    return process.env.VUE_APP_GMAPSAPIKEY ?? ''
  }

  async searchText(query: string, bias?: { lat: number; lng: number }): Promise<PlaceCandidate[]> {
    const body: Record<string, unknown> = {
      textQuery: query,
      languageCode: 'pt-BR',
      regionCode: 'BR',
      maxResultCount: 5
    }
    if (bias) {
      body.locationBias = {
        circle: { center: { latitude: bias.lat, longitude: bias.lng }, radius: 50000 }
      }
    }

    let response
    try {
      response = await axios.post<SearchTextResponse>(this.url, body, {
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': this.apiKey,
          'X-Goog-FieldMask':
            'places.id,places.displayName,places.formattedAddress,places.location,places.types'
        }
      })
    } catch (e) {
      // 403 = API não habilitada/chave sem permissão; 400 = requisição recusada pelo Places.
      const status = getApiErrorStatus(e)
      throw new ServiceError(
        status === 403 || status === 400
          ? PLACES_UNAVAILABLE_MESSAGE
          : PLACES_SEARCH_FAILED_MESSAGE,
        e
      )
    }

    return (response.data.places ?? [])
      .filter((p) => p.location)
      .map((p) => ({
        id: p.id,
        name: p.displayName?.text ?? '',
        address: p.formattedAddress ?? '',
        lat: p.location!.latitude,
        lng: p.location!.longitude,
        types: p.types ?? [],
        confidence: ((p.types ?? []).some((t) => BUILDING_TYPES.includes(t))
          ? 'BUILDING'
          : 'APPROXIMATE') as PinConfidence
      }))
  }
}
